"use client";

import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import TFBadge from "@/Web/Components/TFBadge/TFBadge";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import TFLink from "@/Web/Components/TFLink/TFLink";
import apiCall from "@/libs/api";
import { useMemo, useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

const NewsCard = ({
  news,
  href,
}: {
  news: APIDataSpacesNews;
  href: string;
}) => {
  const t = useTranslations("web.pages.home.sections.news");
  const date = new Date(news.dates.created);
  const formattedDate = date.toLocaleDateString();

  return (
    <TFLink
      href={href}
      className="bg-black/5 rounded-xl p-4 shadow-sm hover:bg-black/10 transition-colors h-full flex flex-col group"
    >
      <div className="flex items-start justify-between mb-3">
        {news.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {news.tags.slice(0, 2).map((tag) => (
              <TFBadge key={tag.ref} title={tag.title} />
            ))}
          </div>
        )}
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-3 line-clamp-2 group-hover:text-black/70 transition-colors">
        {news.title}
      </h3>
      <p className="text-sm text-slate-600 mb-4 line-clamp-3 flex-grow">
        {news.description}
      </p>
      <div className="flex flex-col gap-2 text-xs text-slate-500">
        <span>{formattedDate}</span>
      </div>
    </TFLink>
  );
};

export default function SectionNews() {
  const t = useTranslations("web.pages.home.sections.news");
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "en";
  const [itemsPerView, setItemsPerView] = useState(3);
  const [news, setNews] = useState<APIDataSpacesNews[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 3);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  useEffect(() => {
    const loadNews = async () => {
      try {
        setIsLoading(true);
        const response = await apiCall("hub/spaces/news", {
          action: "publicList",
          locale: locale as UILocale,
        });
        setNews((response as any).news);
      } catch (error) {
        console.error("Failed to load news:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (locale) {
      loadNews();
    }
  }, [locale]);

  const latestNews = useMemo(() => {
    return [...news]
      .sort(
        (a, b) =>
          new Date(b.dates.created).getTime() -
          new Date(a.dates.created).getTime(),
      )
      .slice(0, 6);
  }, [news]);

  if (isLoading || latestNews.length === 0) {
    return null;
  }

  return (
    <TFLayoutHomeSection
      title={t("title")}
      // description="О платформе, функциях и возможностях Tiferet"
      moreButton={{
        title: t("allNews"),
        href: "/news",
      }}
    >
      <TFHorizontalSlider
        items={latestNews}
        renderItem={(newsItem) => (
          <NewsCard news={newsItem} href={`/news/${newsItem.ref}`} />
        )}
        gap={16}
        autoScroll={false}
        showControls={true}
        showDots={true}
        itemsPerView={itemsPerView}
      />
    </TFLayoutHomeSection>
  );
}
